import { PureComponent } from 'react';
import {
  Field,
  SubmissionError,
  formValues,
  reduxForm,
} from 'redux-form/immutable';
import { withAppProps } from 'utils/react/withAppProps';
import { GROUP_SUMMARY } from '../../formName';

import { isNonNullable } from '@ipfabric/utils';
import { Button } from 'components/Button/Button';
import { produce } from 'immer';
import get from 'lodash/get';
import PropTypes from 'prop-types';
import { assoc, equals } from 'ramda';
import { handleSubmitFail } from 'utils/formValidation';
import handleEvent from 'utils/handleEvent';
import OverviewPropTypes from '../../../OverviewPropTypes';
import AbsoluteField from './AbsoluteField';
import AddToGroupArrow from './AddToGroupArrow';
import DisplayTypeField from './DisplayTypeField';
import ReportsWidget from './Groups/ReportsWidget';
import TitleWidget from './Groups/TitleWidget';
import TreeView from './TreeView';

// biome-ignore lint/suspicious/noGlobalIsNan: unsafe to fix in untyped code, fix with refactoring to types
const isNumeric = (n) => !isNaN(Number.parseFloat(n)) && Number.isFinite(n);

@withAppProps
@reduxForm({
  form: GROUP_SUMMARY,
  enableReinitialize: false,
  onSubmitFail: handleSubmitFail('btn-save'),
})
@formValues('group', 'name', 'absolute')
export default class WidgetOptions extends PureComponent {
  static propTypes = {
    absolute: PropTypes.oneOfType([PropTypes.string, PropTypes.bool]),
    change: PropTypes.func.isRequired,
    Chart: OverviewPropTypes.Chart,
    columnIndex: OverviewPropTypes.columnIndex,
    editWidget: OverviewPropTypes.editWidget,
    group: PropTypes.string,
    handleSubmit: PropTypes.func.isRequired,
    menuTree: PropTypes.array.isRequired,
    msg: PropTypes.object.isRequired,
    name: PropTypes.string,
    onClose: PropTypes.func.isRequired,
    onCreateReportGroup: PropTypes.func.isRequired,
    onFetchReports: PropTypes.func.isRequired,
    onSubmit: PropTypes.func.isRequired,
    onToggleConfirmDialog: PropTypes.func.isRequired,
    onUpdateReportGroup: PropTypes.func.isRequired,
    reports: OverviewPropTypes.reports,
    reportsGroups: OverviewPropTypes.reportsGroups,
    rowIndex: OverviewPropTypes.rowIndex,
    submitting: PropTypes.bool,
    untouch: PropTypes.func.isRequired,
    widgetName: PropTypes.string.isRequired,
    widgetProps: OverviewPropTypes.widgetProps,
  };

  constructor(props) {
    super(props);

    this.state = {
      selectedReport: null,
      showTree: false,
      reportsInGroup: this.getSelectedReports(props),
    };
  }

  componentDidMount() {
    const { onFetchReports, change } = this.props;
    const name = this.getSelectedName();
    if (name !== '') {
      change('name', name);
    }
    this.setState({ showTree: true });
    onFetchReports();
  }

  getSelectedName = () => {
    const { reportsGroups, editWidget, widgetProps } = this.props;
    if (editWidget && widgetProps && isNonNullable(widgetProps.group)) {
      const foundGroup = reportsGroups.find((g) => g.id === widgetProps.group);
      if (foundGroup) {
        return foundGroup.name;
      }
    }
    return '';
  };

  getSelectedReports = (props) => {
    const { reportsGroups, editWidget, widgetProps } = props;
    if (editWidget && widgetProps && isNonNullable(widgetProps.group)) {
      const foundGroup = reportsGroups.find((g) => g.id === widgetProps.group);
      if (foundGroup) {
        return foundGroup.children;
      }
    }
    return [];
  };

  getReports = () => {
    const { reports } = this.props;
    return reports.reduce((acc, report) => {
      const { webEndpoint } = report;
      if (acc[webEndpoint]) {
        return produce(acc, (draft) => {
          draft[webEndpoint].push(report);
        });
      }

      return produce(acc, (draft) => {
        draft[webEndpoint] = [report];
      });
    }, {});
  };

  getReportsGroups = () => {
    const { reportsGroups, editWidget, widgetProps, name } = this.props;
    const { reportsInGroup } = this.state;
    if (editWidget && widgetProps && isNonNullable(widgetProps.group)) {
      const foundIndex = reportsGroups.findIndex(
        (g) => g.id === widgetProps.group,
      );
      if (foundIndex > -1) {
        return produce(reportsGroups, (draft) => {
          draft[foundIndex].name = isNonNullable(name) ? name : '';
          draft[foundIndex].children = reportsInGroup;
        });
      }
    }
    return [
      {
        name,
        children: reportsInGroup,
        id: 'tmp',
      },
    ];
  };

  handleSelectReport = handleEvent((selectedReport) => {
    if (this.state.selectedReport === selectedReport) {
      return this.setState({ selectedReport: null });
    }
    return this.setState({ selectedReport });
  });

  handleSelectReportAddToGroup = handleEvent((selectedReport) => {
    this.setState(
      produce((draft) => {
        draft.reportsInGroup.push({ id: selectedReport, weight: 1 });
        draft.selectedReport = null;
      }),

      () => {},
    );
    this.props.untouch('reports');
  });

  handleAddReportToGroup = handleEvent(() => {
    const { selectedReport } = this.state;
    this.setState(
      produce((draft) => {
        draft.reportsInGroup.push({ id: selectedReport, weight: 1 });
        draft.selectedReport = null;
      }),
    );
    this.props.untouch('reports');
  });

  handleDeleteReportFromGroup = handleEvent((reportId) => {
    const { reportsInGroup } = this.state;
    const foundIndex = reportsInGroup.findIndex((r) => r.id === reportId);
    if (foundIndex > -1) {
      this.setState(
        produce((draft) => {
          draft.reportsInGroup.splice(foundIndex, 1);
        }),
      );
    }
  });

  handleSortReport = handleEvent((sourceIndex, destinationIndex) => {
    const { reportsInGroup } = this.state;
    const reportToMove = reportsInGroup[sourceIndex];
    this.setState(
      produce((draft) => {
        draft.reportsInGroup.splice(sourceIndex, 1);
        draft.reportsInGroup.splice(destinationIndex, 0, reportToMove);
      }),
    );
  });

  handleWeightChange = handleEvent((reportIndex, weight) => {
    this.setState(
      produce((draft) => {
        draft.reportsInGroup[reportIndex].weight = Number(weight);
      }),
    );

    this.props.untouch('reports');
  });

  handleSubmitTable = handleEvent((e) => {
    return this.props.handleSubmit((values) =>
      this.handleSubmit(values.set('displayType', 'table')),
    )(e);
  });

  handleSubmitStackedBarChart = handleEvent((e) => {
    return this.props.handleSubmit((values) =>
      this.handleSubmit(values.set('displayType', 'stackedBarChart')),
    )(e);
  });

  handleSubmitPieChart = handleEvent((e) => {
    return this.props.handleSubmit((values) =>
      this.handleSubmit(values.set('displayType', 'pieChart')),
    )(e);
  });

  handleSubmit = handleEvent((values) => {
    const {
      widgetName,
      editWidget,
      onCreateReportGroup,
      widgetProps,
      onUpdateReportGroup,
      rowIndex,
      columnIndex,
      reportsGroups,
    } = this.props;
    const { reportsInGroup } = this.state;
    const errors = {};
    const name = values.get('name');
    const displayType = values.get('displayType');
    if (!name) {
      errors.name = 'requiredName';
    }
    if (!reportsInGroup.length) {
      errors.reports = 'requiredReports';
    } else {
      const invalidReportWeights = reportsInGroup.reduce((acc, val) => {
        const { weight, id } = val;
        if (!isNumeric(weight)) {
          return acc.concat(id);
        }
        if (weight < 1) {
          return acc.concat(id);
        }
        return acc;
      }, []);
      if (invalidReportWeights.length) {
        errors.reports = `positiveWeight-${invalidReportWeights.join(',')}`;
      }
    }
    if (!displayType) {
      errors.displayType = 'required';
    }
    if (Object.keys(errors).length) {
      throw new SubmissionError(errors);
    }
    if (!editWidget) {
      onCreateReportGroup({
        group: { name, children: reportsInGroup },
        rowIndex,
        columnIndex,
        widgetName,
        values: values.toJS(),
      });
    } else {
      const updatedGroup = {
        id: widgetProps.group,
        name,
        children: reportsInGroup,
      };
      const foundGroup = reportsGroups.find((g) => g.id === widgetProps.group);
      if (foundGroup) {
        const comparableGroup = {
          id: foundGroup.id,
          name: foundGroup.name,
          children: foundGroup.children,
        };
        if (!equals(updatedGroup, comparableGroup)) {
          onUpdateReportGroup({ group: updatedGroup });
        }
      }
    }
    this.setState({ showTree: false }, () =>
      this.props.onSubmit(values.toJS(), widgetName, 'widget', !editWidget),
    );
  });

  handleClose = handleEvent((e) => {
    e.preventDefault();
    this.setState({ showTree: false }, () => this.props.onClose());
  });

  render() {
    const {
      absolute,
      Chart,
      editWidget,
      group,
      handleSubmit,
      menuTree,
      msg,
      onCreateReportGroup,
      onToggleConfirmDialog,
      reports,
      submitting,
    } = this.props;
    const reportsGroups = this.getReportsGroups();
    const { selectedReport, showTree, reportsInGroup } = this.state;
    const reportsMap = reports?.reduce((acc, report) => {
      return assoc(report.id, report, acc);
    }, {});
    return (
      <div>
        <form>
          <fieldset disabled={submitting}>
            <div className='dashboard-color-form__modify-groups-wrapper'>
              {showTree ? (
                <div className='dashboard-color-form__widget-options-field-wrapper'>
                  <h4>{get(msg, ['dialog', 'groupSummary', 'widgetTitle'])}</h4>
                  <Field
                    component={TitleWidget}
                    id={`${GROUP_SUMMARY}-name`}
                    msg={msg}
                    name='name'
                    type='text'
                  />
                  <Field
                    component={ReportsWidget}
                    data={menuTree}
                    id={`${GROUP_SUMMARY}-reports`}
                    msg={msg}
                    name='reports'
                    onCreateReportGroup={onCreateReportGroup}
                    onDeleteReportFromGroup={this.handleDeleteReportFromGroup}
                    onDragReport={this.handleSelectReportAddToGroup}
                    onSortReport={this.handleSortReport}
                    onToggleConfirmDialog={onToggleConfirmDialog}
                    onWeightChange={this.handleWeightChange}
                    reports={reports}
                    reportsGroups={reportsGroups}
                    reportsInGroup={reportsInGroup}
                    selectedReport={selectedReport}
                    type='text'
                  />
                </div>
              ) : null}
              <AddToGroupArrow
                onAdd={this.handleAddReportToGroup}
                reportsInGroup={reportsInGroup}
                selectedReport={selectedReport}
              />
              {showTree ? (
                <TreeView
                  data={menuTree}
                  fixedHeight
                  onSelectReport={this.handleSelectReport}
                  onSelectReportAddToGroup={this.handleSelectReportAddToGroup}
                  reportsMap={reportsMap}
                  reportsList={this.getReports()}
                  reportsGroups={reportsGroups}
                  reportsInGroup={reportsInGroup}
                  selectedGroup={group}
                  selectedReport={selectedReport}
                  msg={msg}
                />
              ) : null}
            </div>
            {reportsInGroup.length ? (
              <div>
                <div style={{ display: 'flex', alignItems: 'center' }}>
                  <h4 className='inline-block'>
                    {get(msg, ['dialog', 'groupSummary', 'selectWidgetView'])}
                  </h4>
                  <Field
                    component={AbsoluteField}
                    msg={msg}
                    name='absolute'
                    type='checkbox'
                  />
                </div>
                <div className='dashboard-color-form__row row'>
                  <Field
                    absolute={absolute || false}
                    Chart={Chart}
                    component={DisplayTypeField}
                    data={{ reportsGroups, reportsMap }}
                    displayType='table'
                    editWidget={editWidget}
                    group={group}
                    msg={msg}
                    name='displayType'
                    onSelect={this.handleSubmitTable}
                    type='radio'
                    value='table'
                  />
                  <Field
                    absolute={absolute || false}
                    Chart={Chart}
                    component={DisplayTypeField}
                    data={{ reportsGroups, reportsMap }}
                    displayType='stackedBarChart'
                    editWidget={editWidget}
                    group={group}
                    msg={msg}
                    name='displayType'
                    onSelect={this.handleSubmitStackedBarChart}
                    type='radio'
                    value='stackedBarChart'
                  />
                  <Field
                    absolute={absolute || false}
                    Chart={Chart}
                    component={DisplayTypeField}
                    data={{ reportsGroups, reportsMap }}
                    displayType='pieChart'
                    editWidget={editWidget}
                    group={group}
                    msg={msg}
                    name='displayType'
                    onSelect={this.handleSubmitPieChart}
                    type='radio'
                    value='pieChart'
                  />
                </div>
              </div>
            ) : null}
          </fieldset>
        </form>
        <div className='dashboard__add-widget-footer'>
          {editWidget ? (
            <Button
              variant='primary'
              onClick={handleSubmit(this.handleSubmit)}
              type='button'
              icon='fa-check'
              className='mr-2'
            >
              {get(msg, ['dialog', 'btnUpdate'])}
            </Button>
          ) : null}
          <Button
            variant='secondary'
            onClick={this.handleClose}
            type='button'
            icon='fa-xmark'
          >
            {get(msg, ['dialog', 'btnBack'])}
          </Button>
        </div>
      </div>
    );
  }
}
